/**
 * @license
 * Copyright 2025 Millahue.
 * @constant {string} SECRET_PASSPHRASE
 * @description Contraseña secreta para cifrado
 *  - Esta contraseña se utiliza para proteger la información sensible mediante cifrado.
 *  - En producción, se recomienda utilizar una contraseña única y secreta.
 *  - En desarrollo, se puede utilizar una contraseña de ejemplo.
 *  - En este caso, se utiliza una contraseña de ejemplo para fines de demostración.
 *  - En una aplicación real, asegúrate de utilizar una contraseña fuerte y única.
 */
const defaultSecret = 'key-dev@millahue'

/**
 * @constant {string} SECRET_PASSPHRASE
 * @description Contraseña secreta para cifrado
 *
 * @see {@link https://owasp.org/www-community/OWASP_Cheat_Sheet_Series/Password_Storage_Cheat_Sheet OWASP Password Storage Cheat Sheet}
 */
export const SECRET_PASSPHRASE = process.env.SECRET_PASSPHRASE || defaultSecret
/**
 * @constant {string} CRYPTO_SALT
 * @description Sal secreta para la derivación de claves
 * - Esta sal se utiliza para derivar la clave de cifrado a partir de la contraseña secreta.
 * - En producción, se recomienda utilizar una CRYPTO_SALT única y secreta.
 * - En desarrollo, se puede utilizar una CRYPTO_SALT de ejemplo.
 * @see {@link https://owasp.org/www-community/OWASP_Cheat_Sheet_Series/Password_Storage_Cheat_Sheet OWASP Password Storage Cheat Sheet}
 */
const CRYPTO_SALT = process.env.CRYPTO_SALT || defaultSecret

/**
 * @constant {Uint8Array} SALT
 * @description Salt value for PBKDF2 key derivation.
 * This is a 16-byte salt that should be unique per application.
 *
 * In production:
 * - The salt is read from environment variable CRYPTO_SALT
 * - Falls back to a default value for development
 * - While salt doesn't need to be secret, it should be application-specific
 *
 * @example
 * // In .env file:
 * CRYPTO_SALT=your-unique-salt-value
 *
 * @see {@link https://www.owasp.org/index.php/Password_Storage_Cheat_Sheet OWASP Password Storage Cheat Sheet}
 * @see {@link https://nvlpubs.nist.gov/nistpubs/SpecialPublications/NIST.SP.800-132.pdf NIST SP 800-132}
 */
export const SALT = CRYPTO_SALT && Uint8Array.from(CRYPTO_SALT.split('').map(c => c.charCodeAt(0)))

/**
 * @constant {number} ITERATIONS
 * @description Número de iteraciones para PBKDF2 (Password-Based Key Derivation Function 2).
 * Este valor determina cuántas veces se aplica internamente una función pseudoaleatoria (generalmente basada en HMAC)
 * para derivar la clave de cifrado a partir de la frase de contraseña y la sal.
 * El propósito es hacer que el proceso de derivación de claves sea computacionalmente costoso,
 * lo que ralentiza significativamente los ataques de fuerza bruta o de diccionario.
 *
 * Impacto de los valores:
 * - **Valores Mínimos (e.g., 1, 1000):**
 *   - **Ventajas:** La derivación de la clave es muy rápida, lo que resulta en una respuesta casi instantánea para el usuario.
 *   - **Desventajas:** Seguridad muy baja. Un número bajo de iteraciones permite a los atacantes probar una gran cantidad
 *     de contraseñas candidatas por segundo utilizando hardware moderno (CPUs, GPUs, ASICs).
 *     Un valor de 1 es extremadamente inseguro y no ofrece prácticamente ninguna resistencia contra ataques.
 *   - **Ejemplo:** Si `ITERATIONS = 1000`, un atacante podría probar millones de contraseñas por segundo en un sistema potente,
 *     haciendo que las contraseñas débiles o comunes sean vulnerables en cuestión de minutos u horas.
 *
 * - **Valores Máximos (e.g., 500,000, 1,000,000 o más):**
 *   - **Ventajas:** Seguridad muy alta. Cada intento de adivinar una contraseña se vuelve muy lento para el atacante,
 *     haciendo que los ataques de fuerza bruta sean prohibitivamente largos (años, décadas o más).
 *   - **Desventajas:** La derivación de la clave también se vuelve lenta para el usuario legítimo. Esto puede llevar a
 *     retrasos notables (por ejemplo, durante el inicio de sesión, el cifrado o el descifrado de datos),
 *     lo que podría afectar negativamente la experiencia del usuario, especialmente en dispositivos con menos potencia.
 *     También puede aumentar la carga en el servidor si la derivación de claves se realiza allí para múltiples usuarios.
 *   - **Ejemplo:** Si `ITERATIONS = 1,000,000`, la derivación de la clave podría tardar desde unos cientos de milisegundos
 *     hasta varios segundos, dependiendo del hardware. Si bien es seguro, puede ser frustrante para el usuario si la espera es demasiado larga.
 *
 * - **Valores Recomendados (Balance):**
 *   - El objetivo es encontrar un equilibrio: el número de iteraciones debe ser lo suficientemente alto para que
 *     un ataque de fuerza bruta sea inviable con la tecnología actual y previsible en el corto plazo,
 *     pero no tan alto como para causar una degradación inaceptable del rendimiento para el usuario.
 *   - Las recomendaciones de organizaciones como OWASP (Open Web Application Security Project) o NIST (National Institute of Standards and Technology)
 *     sugieren valores que aumentan con el tiempo a medida que el poder de cómputo general mejora.
 *     Por ejemplo, OWASP ha recomendado valores como 310,000 para PBKDF2-HMAC-SHA256 (en 2023) o incluso más para SHA512.
 *     El valor de `100000` (como en este código) es un punto de partida razonable para aplicaciones cliente,
 *     pero siempre debe evaluarse en el contexto específico de la aplicación, la sensibilidad de los datos
 *     y las recomendaciones de seguridad más recientes.
 *   - Es crucial revisar y potencialmente aumentar este valor periódicamente (por ejemplo, cada 1-2 años o cuando
 *     las guías de seguridad se actualicen) para mantener un nivel de seguridad adecuado contra el avance del hardware.
 *
 * En resumen:
 *   - **Más iteraciones:** Mayor seguridad (más difícil para los atacantes), pero mayor tiempo de procesamiento (más lento para los usuarios).
 *   - **Menos iteraciones:** Menor seguridad (más fácil para los atacantes), pero menor tiempo de procesamiento (más rápido para los usuarios).
 *
 * La elección del número de iteraciones es una compensación crítica entre seguridad y usabilidad.
 * @see {@link https://www.owasp.org/index.php/Password_Storage_Cheat_Sheet OWASP Password Storage Cheat Sheet}
 * @see {@link https://nvlpubs.nist.gov/nistpubs/SpecialPublications/NIST.SP.800-132.pdf NIST SP 800-132}
 */
export const ITERATIONS = 100000 // Número de iteraciones para PBKDF2 (más es más seguro)

/**
 * @constant {number} KEY_LENGTH
 * @description Longitud de la clave AES en bits. AES admite longitudes de 128, 192 o 256 bits.
 * En este caso, se utiliza AES-256, que es más seguro que AES-128 o AES-192.
 * Longitud de la clave AES en bits para el cifrado AES-GCM:
 * - Es más seguro que AES-128 y suficiente para la mayoría de aplicaciones
 * - Es el estándar recomendado actualmente por NIST
 * - AES-512 no existe, las únicas opciones válidas son 128, 192 y 256 bits
 * - Ofrece un buen balance entre seguridad y rendimiento
 * @see {@link https://nvlpubs.nist.gov/nistpubs/FIPS/NIST.FIPS.197.pdf FIPS PUB 197}
 * @see {@link https://csrc.nist.gov/publications/detail/sp/800-38d/final NIST SP 800-38D}
 */
export const KEY_LENGTH = 256 // Longitud de la clave AES en bits (AES-256)

/**
 * @constant {number} IV_LENGTH_BYTES
 * @description Longitud recomendada del vector de inicialización (IV) en bytes para AES-GCM.
 * AES-GCM recomienda un IV de 12 bytes (96 bits) para un equilibrio óptimo entre seguridad y eficiencia.
 *
 * Length of the Initialization Vector (IV) in bytes for AES-GCM encryption
 *
 * @default 12
 *
 * @description
 * - 12 bytes (96 bits) is the NIST recommended length for AES-GCM
 * - Minimum length: 1 byte (not recommended)
 * - Maximum length: No theoretical limit, but larger values impact performance
 *
 * @example
 * Advantages of 12 bytes:
 * - Optimal performance (hardware acceleration)
 * - NIST standard compliance
 * - Sufficient security for most applications
 *
 * Disadvantages of other lengths:
 * < 12 bytes: Security risks, vulnerable to attacks
 * > 12 bytes: Performance overhead without significant security benefits
 *
 * @see {@link https://nvlpubs.nist.gov/nistpubs/Legacy/SP/nistspecialpublication800-38d.pdf NIST SP 800-38D}
 */
export const IV_LENGTH_BYTES = 12 // Longitud recomendada del IV en bytes para AES-GCM
